import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  CircularProgress,
  IconButton,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UndoIcon from '@mui/icons-material/Undo';
import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { SignatureRequirementClient } from 'clients/signature-requirement-client';
import { ISignatureRequirement } from 'models/signature-requirement/types';
import { groupRulesByTrigger, formatInsuranceTrigger } from './table-utils';
import { formatDate } from './utils';
import { TRIGGER_TYPE_LABELS } from './constants';
import { SignatureRequirementTableStyles } from './signature-requirement-table.styles';

interface ITableProps {
  searchTerm: string | undefined;
  selectedCustomerId: number;
  // debouncedTriggerTypes: pre-debounced (300ms) by index.tsx — triggers a full API refetch on change.
  // Single type → sent as trigger_type to API. Multiple/All → trigger_type omitted, API returns all.
  debouncedTriggerTypes: string[];
  userHasPermission: boolean;
  // Lifted state: parent owns checked IDs so it can enable/disable the Exclude button.
  setCheckedRuleIds: (ids: number[]) => void;
  // Expose loaded rules to parent for delete modal
  onDataLoaded?: (rules: ISignatureRequirement[]) => void;
  reloadDataTrigger?: Date; // Can be used by parent to force reload when needed (e.g. after adding a new rule)
}

type ISignatureRequirementTableProps = ITableProps &
  WithStyles<typeof SignatureRequirementTableStyles>;

const SignatureRequirementTableComponent: React.FC<ISignatureRequirementTableProps> = props => {
  const {
    classes,
    selectedCustomerId,
    debouncedTriggerTypes,
    searchTerm,
    onDataLoaded,
    reloadDataTrigger,
  } = props;
  const [rawData, setRawData] = React.useState<ISignatureRequirement[]>([]);
  const [checkedItems, setCheckedItems] = React.useState<Record<string, number[]>>({});
  const [loading, setLoading] = React.useState<boolean>(false);
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(new Set());
  const [restoringId, setRestoringId] = React.useState<number | null>(null);

  const users = useTypedSelector(state => state.lookups.users);

  const loadData = React.useCallback(async () => {
    if (!selectedCustomerId) return;

    setLoading(true);
    try {
      // Send trigger_type only when exactly one specific type is selected.
      const isSingleType =
        debouncedTriggerTypes.length === 1 && !debouncedTriggerTypes.includes('all');
      const triggerType = isSingleType ? debouncedTriggerTypes[0] : undefined;

      const response = await SignatureRequirementClient.list(selectedCustomerId, {
        search: searchTerm,
        trigger_type: triggerType,
      });
      // API returns a flat array (not a paginated wrapper)
      setRawData(response.data);
      // Expose data to parent for delete modal
      if (onDataLoaded) {
        onDataLoaded(response.data);
      }
    } catch (error) {
      // TODO: Add notifyError
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId, searchTerm, debouncedTriggerTypes, onDataLoaded, reloadDataTrigger]);

  // Refetch on any filter change. All filter props are pre-debounced by index.tsx.
  // No client-side filtering — every filter change fetches fresh data from the server.
  React.useEffect(() => {
    loadData();
  }, [loadData]);

  // Group the API response for display. No client-side trigger filtering applied here.
  const displayData = React.useMemo(() => {
    return groupRulesByTrigger(rawData, users);
  }, [rawData, users]);

  // Sync checked IDs back to parent whenever checkedItems changes.
  React.useEffect(() => {
    const allIds = Object.values(checkedItems).flat();
    props.setCheckedRuleIds(allIds);
  }, [checkedItems, props]);

  const handleParentCheckboxChange = (triggerType: string, childIds: number[]) => {
    const currentChecked = checkedItems[triggerType] || [];
    if (currentChecked.length === childIds.length) {
      // All checked → uncheck all
      const newChecked = { ...checkedItems };
      delete newChecked[triggerType];
      setCheckedItems(newChecked);
    } else {
      // Some or none checked → check all
      setCheckedItems({
        ...checkedItems,
        [triggerType]: childIds,
      });
    }
  };

  const handleChildCheckboxChange = (triggerType: string, ruleId: number) => {
    const currentChecked = checkedItems[triggerType] || [];
    const isChecked = currentChecked.includes(ruleId);

    if (isChecked) {
      // Uncheck
      const newChecked = currentChecked.filter(id => id !== ruleId);
      if (newChecked.length === 0) {
        const newItems = { ...checkedItems };
        delete newItems[triggerType];
        setCheckedItems(newItems);
      } else {
        setCheckedItems({
          ...checkedItems,
          [triggerType]: newChecked,
        });
      }
    } else {
      // Check
      setCheckedItems({
        ...checkedItems,
        [triggerType]: [...currentChecked, ruleId],
      });
    }
  };

  const handleToggleGroup = (triggerType: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(triggerType)) {
      newExpanded.delete(triggerType);
    } else {
      newExpanded.add(triggerType);
    }
    setExpandedGroups(newExpanded);
  };

  const handleRestore = async (ruleId: number) => {
    setRestoringId(ruleId);
    try {
      await SignatureRequirementClient.restore(ruleId);
      await loadData();
    } catch (error) {
      // TODO: Add notifyError
    } finally {
      setRestoringId(null);
    }
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  if (displayData.length === 0) {
    return (
      <div className={classes.emptyState}>
        {selectedCustomerId === 0
          ? 'Please select a customer'
          : 'No signature requirement rules found'}
      </div>
    );
  }

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table>
        <TableHead>
          <TableRow>
            {props.userHasPermission && <TableCell padding="checkbox" />}
            <TableCell width="40%">Rule Name</TableCell>
            <TableCell width="30%">Trigger Type</TableCell>
            <TableCell width="20%">Created By</TableCell>
            <TableCell width="20%">Last Modified</TableCell>
            <TableCell width="12px" />
          </TableRow>
        </TableHead>
        <TableBody>
          {displayData.map(group => {
            const childIds = group.children.map(child => child.id);
            const currentChecked = checkedItems[group.triggerType] || [];
            const isAllChecked = currentChecked.length === childIds.length && childIds.length > 0;
            const isSomeChecked =
              currentChecked.length > 0 && currentChecked.length < childIds.length;
            const isSingleItem = group.children.length === 1;
            const isExpanded = expandedGroups.has(group.triggerType);

            // For single-item groups, merge child details into parent row
            const singleRule = isSingleItem ? group.children[0] : null;
            let singleRuleDisplayName = null;
            if (singleRule) {
              if (singleRule.trigger_type === 'insurance') {
                singleRuleDisplayName = formatInsuranceTrigger(
                  singleRule.insurance_bin ?? null,
                  singleRule.insurance_pcn ?? null,
                  singleRule.insurance_group ?? null,
                );
              } else {
                singleRuleDisplayName = singleRule.name;
              }
            }

            return (
              <React.Fragment key={group.triggerType}>
                {/* Parent Row */}
                <TableRow
                  className={classes.parentRow}
                  sx={
                    singleRule?.is_pending_delete ? { borderLeft: '3px solid #ed6c02' } : undefined
                  }
                >
                  {props.userHasPermission && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isAllChecked}
                        indeterminate={isSomeChecked}
                        onChange={() => handleParentCheckboxChange(group.triggerType, childIds)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {isSingleItem ? singleRuleDisplayName : `${group.children.length} rule(s)`}
                    {isSingleItem && singleRule?.is_draft ? (
                      <Chip
                        label="Draft"
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ ml: 1, fontSize: '0.65rem', height: 18 }}
                      />
                    ) : (
                      <></>
                    )}
                    {isSingleItem && singleRule?.is_pending_delete ? (
                      <Chip
                        label="Pending removal"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ ml: 1, fontSize: '0.65rem', height: 18 }}
                      />
                    ) : (
                      <></>
                    )}
                  </TableCell>
                  <TableCell>{TRIGGER_TYPE_LABELS[group.triggerType]}</TableCell>
                  <TableCell>
                    {isSingleItem && singleRule
                      ? users.find(u => u.id === singleRule.created_by)?.display_name ??
                        `User ${singleRule.created_by}`
                      : group.createdBy}
                  </TableCell>
                  <TableCell>
                    {isSingleItem && singleRule
                      ? formatDate(singleRule.updated_ts)
                      : formatDate(group.lastModified)}
                  </TableCell>
                  <TableCell>
                    {!isSingleItem && (
                      <IconButton
                        size="small"
                        onClick={() => handleToggleGroup(group.triggerType)}
                        style={{ marginRight: 8 }}
                      >
                        {isExpanded ? <ChevronRightIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    )}
                    {isSingleItem && singleRule?.is_pending_delete && props.userHasPermission ? (
                      <IconButton
                        size="small"
                        title="Restore rule"
                        disabled={restoringId === singleRule.id}
                        onClick={() => handleRestore(singleRule.id)}
                      >
                        <UndoIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <>-</>
                    )}
                  </TableCell>
                </TableRow>

                {/* Child Rows - only show if expanded and has multiple items */}
                {!isSingleItem &&
                  isExpanded &&
                  group.children.map(rule => {
                    const isChecked = currentChecked.includes(rule.id);
                    const displayName =
                      rule.trigger_type === 'insurance'
                        ? formatInsuranceTrigger(
                            rule.insurance_bin ?? null,
                            rule.insurance_pcn ?? null,
                            rule.insurance_group ?? null,
                          )
                        : ' - ';

                    return (
                      <TableRow
                        key={rule.id}
                        sx={{
                          ...(rule.is_pending_delete
                            ? { borderLeft: '3px solid #ed6c02', opacity: 0.75 }
                            : rule.is_draft
                            ? { opacity: 0.6 }
                            : {}),
                        }}
                      >
                        {props.userHasPermission && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isChecked}
                              onChange={() => handleChildCheckboxChange(group.triggerType, rule.id)}
                            />
                          </TableCell>
                        )}
                        <TableCell className={classes.childRow}>
                          <span
                            style={
                              rule.is_pending_delete
                                ? { textDecoration: 'line-through' }
                                : undefined
                            }
                          >
                            {rule.name}
                          </span>
                          {rule.is_draft ? (
                            <Chip
                              label="Draft"
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ ml: 1, fontSize: '0.65rem', height: 18 }}
                            />
                          ) : (
                            <></>
                          )}
                          {rule.is_pending_delete ? (
                            <Chip
                              label="Pending removal"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ ml: 1, fontSize: '0.65rem', height: 18 }}
                            />
                          ) : (
                            <></>
                          )}
                        </TableCell>
                        <TableCell>{displayName}</TableCell>
                        <TableCell>
                          {users.find(u => u.id === rule.created_by)?.display_name ??
                            `User ${rule.created_by}`}
                        </TableCell>
                        <TableCell>{formatDate(rule.updated_ts)}</TableCell>
                        <TableCell>
                          {rule.is_pending_delete && props.userHasPermission ? (
                            <IconButton
                              size="small"
                              title="Restore rule"
                              disabled={restoringId === rule.id}
                              onClick={() => handleRestore(rule.id)}
                            >
                              <UndoIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <>-</>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const SignatureRequirementTable = withStyles(SignatureRequirementTableStyles)(
  SignatureRequirementTableComponent,
);
